import { useCallback, useEffect, useRef } from "react";
import { useUserStore } from "../stores/userStore";
import {
  sendMessageInteractionStats,
  MessageInteractionStats,
} from "../axios/chat";
import { Message } from "../types";

// Batching mechanism to avoid overwhelming backend
const statsQueue: MessageInteractionStats[] = [];
let batchTimeout: NodeJS.Timeout | null = null;
let isProcessing = false;
const BATCH_DELAY_MS = 1000;
const MAX_BATCH_SIZE = 5;

const flushStatsQueue = async () => {
  if (statsQueue.length === 0 || isProcessing) {
    if (statsQueue.length > 0 && !isProcessing) {
      batchTimeout = setTimeout(flushStatsQueue, BATCH_DELAY_MS);
    } else {
      batchTimeout = null;
    }
    return;
  }

  isProcessing = true;
  const batch = statsQueue.splice(0, MAX_BATCH_SIZE);

  for (const stats of batch) {
    try {
      await sendMessageInteractionStats(stats);
      await new Promise((resolve) => setTimeout(resolve, 50));
    } catch (error) {
      console.error("Error sending message interaction stats:", error);
    }
  }

  isProcessing = false;

  if (statsQueue.length > 0) {
    batchTimeout = setTimeout(flushStatsQueue, BATCH_DELAY_MS);
  } else {
    batchTimeout = null;
  }
};

const queueStats = (stats: MessageInteractionStats) => {
  statsQueue.push(stats);

  if (!batchTimeout) {
    batchTimeout = setTimeout(flushStatsQueue, BATCH_DELAY_MS);
  }
};

const isRecentMessage = (messageDate: string): boolean => {
  try {
    const messageTime = new Date(messageDate).getTime();
    const now = Date.now();
    const timeDiffSeconds = (now - messageTime) / 1000;
    return timeDiffSeconds <= 10;
  } catch {
    return false;
  }
};

export const useMessageInteractionTracking = (
  messageOrId: Message | string | null,
  elementRef?: React.RefObject<HTMLElement | null>
) => {
  const { user } = useUserStore();
  const viewStartTimeRef = useRef<number | null>(null);
  const isVisibleRef = useRef<boolean>(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const message = typeof messageOrId === "object" ? messageOrId : null;
  const messageId =
    typeof messageOrId === "string" ? messageOrId : message?.message_id || "";

  // Skip tracking if:
  // 1. No message/messageId provided
  // 2. System message (only if we have full message object)
  // 3. Message is not recent (only if we have full message object with date)
  const shouldTrack = useCallback(() => {
    if (!messageId) return false;
    if (message) {
      if (message.message_type === "system") return false;
      if (!isRecentMessage(message.date)) return false;
    }
    return true;
  }, [message, messageId]);

  const trackInteraction = useCallback(
    (action: string, duration: number = 0) => {
      if (!user?.id || !shouldTrack()) return;

      const stats: MessageInteractionStats = {
        message_id: messageId,
        user_id: user.id,
        action,
        duration,
      };

      queueStats(stats);
    },
    [user?.id, messageId, shouldTrack]
  );

  const startViewTracking = useCallback(() => {
    if (!shouldTrack() || !isVisibleRef.current) return;

    const startTime = Date.now();
    viewStartTimeRef.current = startTime;
  }, [shouldTrack]);

  const stopViewTracking = useCallback(() => {
    if (viewStartTimeRef.current && shouldTrack() && isVisibleRef.current) {
      const duration = (Date.now() - viewStartTimeRef.current) / 1000;
      trackInteraction("view", duration);
      viewStartTimeRef.current = null;
    }
  }, [trackInteraction, shouldTrack]);

  useEffect(() => {
    if (!elementRef?.current || !message || !shouldTrack()) return;

    const element = elementRef.current;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const wasVisible = isVisibleRef.current;
          isVisibleRef.current = entry.isIntersecting;

          if (entry.isIntersecting && !wasVisible) {
            startViewTracking();
          }
          else if (!entry.isIntersecting && wasVisible) {
            stopViewTracking();
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: "0px",
      }
    );

    observerRef.current.observe(element);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      stopViewTracking();
    };
  }, [elementRef, message, shouldTrack, startViewTracking, stopViewTracking]);

  useEffect(() => {
    return () => {
      stopViewTracking();
    };
  }, [stopViewTracking]);

  return {
    trackInteraction,
    startViewTracking,
    stopViewTracking,
  };
};
